import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import styled from "styled-components";
import { UserIcon } from "ui/icons";
import { defaultThemeColors, fonts, mediaSizes } from "ui/theme";
import { ButtonPrimary } from "../Button/ButtonStyled";
import { ClassModal } from "./components/ClassModal";
import { Avatar } from "../editAvatar/Avatar";
import { ClassDateCard } from "../dateCard/ClassDateCard";
import { useNavigate } from "react-router-dom";
import { userRoute } from "pages/user/user.routes";

require("moment-timezone");

export const DEFAULT_TIMEZONE = "America/New_York";

export const RequestedClassCard = ({ requestedClass, onCancel, isLoading, sendMessage }) => {
    const [isOverflowing, setIsOverflowing] = useState(false);
    const instructionsRef = useRef(null);
    const navigate = useNavigate()

    const handleNavigation = () => {
        if (!requestedClass?.teacher?.id) return;

        navigate(`${userRoute.classDetail}${requestedClass?.teacher?.id}`)
    }

    const class_type = requestedClass?.class_type
        ? requestedClass?.class_type.toLowerCase()
        : "";

    const isOnline = requestedClass?.online_type === "online"

    const badgeVariant = isOnline ? "black_fade_small" : class_type === "private" ? "primary_fade_small" : "destructive_fade_small"

    const dates = requestedClass?.booking_slots?.map((item) => {
        const momentDateTime = moment.tz(
            `${item.class_date} ${item.class_time}`,
            "YYYY-MM-DD HH:mm:ss",
            item.time_zone || DEFAULT_TIMEZONE
        );

        return {
            id: item.id,
            type: requestedClass?.class_type,
            date: momentDateTime.format("MMM D, YYYY"),
            time: momentDateTime.format("h:mm a"),
            cost: item.total_amount,
        };
    });

    useEffect(() => {
        const el = instructionsRef.current;
        if (el) {
            setIsOverflowing(el.scrollHeight > el.clientHeight);
        }
    }, [requestedClass?.teacher_data?.last_class_instruction]);

    const instruction = requestedClass?.teacher_data?.last_class_instruction;
    return (
        <ClassItem>
            <ClassInfo>
                <Avatar
                    profile={requestedClass?.teacher_data?.profile_pic}
                    alt={requestedClass?.teacher?.first_name}
                    onClick={handleNavigation}
                />

                <InfoContainer>
                    <Info>
                        <Name onClick={handleNavigation}>{requestedClass?.teacher?.first_name}</Name>
                        <Details>
                            <span>
                                <UserIcon color="placeholder" width="15" height="15" />
                            </span>
                            <span>{requestedClass?.student_per_class}</span> |{" "}
                            <span>{requestedClass?.class?.title}</span>
                        </Details>
                    </Info>
                    <div>
                        <ButtonPrimary
                            variant={badgeVariant}
                            style={{
                                pointerEvents: "none",
                            }}
                        >
                            {isOnline ? "Online" : class_type}
                        </ButtonPrimary>
                    </div>
                </InfoContainer>


            </ClassInfo>
            <ExpandedDetails>
                {dates?.map((date) => {
                    return <ClassDateCard
                        key={date.id}
                        date={date}
                        priceBreakdown={{
                            booking_total_amount: requestedClass.booking_total_amount,
                            discount_amount: requestedClass.discount_amount,
                            tax: requestedClass.tax,
                            total_amount: requestedClass.total_amount
                        }}
                        isOnline={isOnline}
                    />;
                })}

                <DetailCol>
                    {/* <Label>Class instructions</Label>
                    <Instructions ref={instructionsRef}>{instruction}</Instructions> */}
                    {isOverflowing && (
                        <ClassModal
                            classData={{
                                class: requestedClass?.teacher_data?.subject_sub_category,
                                rating: requestedClass?.teacher_data?.rate,
                                class_type: requestedClass?.class_type,
                                date: dates[0],
                                class_instruction: instruction,
                                profile_pic: requestedClass?.teacher_data?.profile_pic,
                            }}
                        />
                    )}
                </DetailCol>

                <Actions>
                    <ButtonPrimary variant="secondary" onClick={sendMessage}>Message</ButtonPrimary>
                    <ButtonPrimary
                        disabled={isLoading}
                        onClick={onCancel}
                        variant="primary_fade"
                    >
                        Cancel
                    </ButtonPrimary>
                </Actions>
            </ExpandedDetails>
        </ClassItem>
    );
};


const ClassItem = styled.div`
    border-width: 2px;
    border-style: solid;
    border-color: ${defaultThemeColors.lightBlue};
    background-color: ${defaultThemeColors.white};
    border-radius: 8px;
    padding: 1rem;
    width: 100%;
    display: flex;
    flex-direction: column;

    @media (width >= ${mediaSizes.md}) {
        padding: 1.5rem;
    }
`;

const ClassInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const InfoContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex: 1;
`

const Info = styled.div`
    flex: 1;
    display: grid;
    gap: 0.5rem;
`;

const Name = styled.div`
    font-size: ${fonts.sizes.regular};
    font-weight: bold;
    width: fit-content;
    cursor: pointer;
`;

const Details = styled.div`
    font-size: ${fonts.sizes.small};
    color: ${defaultThemeColors.placeholder};
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.3rem;

    & span:nth-of-type(2) {
        color: ${defaultThemeColors.txt};
    }
`;

const ExpandedDetails = styled.div`
    padding-top: 1rem;
    gap: 0.5rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
`;

const DetailCol = styled.div`
    display: grid;
    gap: 0.3rem;
    /* padding-bottom: 1rem; */
`;

const Label = styled.span`
    color: ${defaultThemeColors.placeholder};
    font-size: ${fonts.sizes.small};
    font-weight: 500;
    white-space: nowrap;
    text-transform: capitalize;
`;

const Instructions = styled.div`
    color: ${defaultThemeColors.txt};
    font-size: ${fonts.sizes.small};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Actions = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;

    > * {
        grid-column: span 1;
    }

    &:has(> *:nth-child(3)) > :first-child {
        grid-column: span 2;
    }

    > :only-child {
        grid-column: span 2;
    }
`;
